import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { defaultTemplates } from '@/components/certificates/CertificatePreview';
import type { CertificateTemplate } from '@/components/certificates/CertificatePreview';
import { toast } from 'sonner';

interface SavedTemplate {
  id: string;
  company_id: string | null;
  name: string;
  config: Record<string, unknown>;
  logo_url: string | null;
  signature_url: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function savedToTemplate(saved: SavedTemplate): CertificateTemplate {
  const config = saved.config as Record<string, unknown>;
  return {
    id: saved.id,
    name: saved.name,
    style: (config.style as CertificateTemplate['style']) || 'modern',
    primaryColor: (config.primaryColor as string) || '#1e3a5f',
    accentColor: (config.accentColor as string) || '#0ea5e9',
    backgroundColor: config.backgroundColor as string | undefined,
    logoUrl: saved.logo_url || (config.logoUrl as string | undefined),
    logoPosition: (config.logoPosition as CertificateTemplate['logoPosition']) || 'top-center',
    signatureUrl: saved.signature_url || (config.signatureUrl as string | undefined),
    borderStyle: (config.borderStyle as CertificateTemplate['borderStyle']) || 'simple',
    fontFamily: (config.fontFamily as CertificateTemplate['fontFamily']) || 'sans',
    certificateTitle: config.certificateTitle as string | undefined,
    companyName: config.companyName as string | undefined,
    issuerName: config.issuerName as string | undefined,
    issuerTitle: config.issuerTitle as string | undefined,
    footerText: config.footerText as string | undefined,
    accreditationText: config.accreditationText as string | undefined,
    isCustom: true,
    companyId: saved.company_id || undefined,
  };
}

function templateToConfig(template: CertificateTemplate): Record<string, unknown> {
  return {
    style: template.style,
    primaryColor: template.primaryColor,
    accentColor: template.accentColor,
    backgroundColor: template.backgroundColor,
    logoUrl: template.logoUrl,
    logoPosition: template.logoPosition,
    signatureUrl: template.signatureUrl,
    borderStyle: template.borderStyle,
    fontFamily: template.fontFamily,
    certificateTitle: template.certificateTitle,
    companyName: template.companyName,
    issuerName: template.issuerName,
    issuerTitle: template.issuerTitle,
    footerText: template.footerText,
    accreditationText: template.accreditationText,
  };
}

export function useCertificateTemplates() {
  const { user } = useAuth();
  const [customTemplates, setCustomTemplates] = useState<CertificateTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const companyId = user?.company_id;

  // Load saved templates
  const loadTemplates = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('company_id', companyId)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      
      const templates = (data as unknown as SavedTemplate[]).map(savedToTemplate);
      setCustomTemplates(templates);
    } catch (err) {
      console.error('Failed to load certificate templates:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Save template
  const saveTemplate = async (
    template: CertificateTemplate,
    name?: string
  ): Promise<CertificateTemplate | null> => {
    if (!companyId) {
      toast.error('No company associated with your account');
      return null;
    }

    try {
      const config = templateToConfig(template);
      
      // If it's an existing custom template, update it
      if (template.isCustom && template.id) {
        const { data, error } = await supabase
          .from('certificate_templates')
          .update({
            name: name || template.name,
            config,
            logo_url: template.logoUrl || null,
            signature_url: template.signatureUrl || null,
          } as Record<string, unknown>)
          .eq('id', template.id)
          .select()
          .single();

        if (error) throw error;
        
        const updated = savedToTemplate(data as unknown as SavedTemplate);
        setCustomTemplates((prev) =>
          prev.map((t) => (t.id === updated.id ? updated : t))
        );
        toast.success('Template updated!');
        return updated;
      }

      // Insert new template
      const { data, error } = await supabase
        .from('certificate_templates')
        .insert({
          company_id: companyId,
          name: name || `Custom ${template.name}`,
          config,
          logo_url: template.logoUrl || null,
          signature_url: template.signatureUrl || null,
          created_by: user?.id || null,
        } as Record<string, unknown>)
        .select()
        .single();

      if (error) throw error;
      
      const saved = savedToTemplate(data as unknown as SavedTemplate);
      setCustomTemplates((prev) => [saved, ...prev]);
      toast.success('Template saved!');
      return saved;
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Failed to save template');
      return null;
    }
  };

  // Delete template
  const deleteTemplate = async (templateId: string) => {
    try {
      const { error } = await supabase
        .from('certificate_templates')
        .delete()
        .eq('id', templateId);

      if (error) throw error;
      
      setCustomTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success('Template deleted');
    } catch (err) {
      console.error('Failed to delete template:', err);
      toast.error('Failed to delete template');
    }
  };

  // Upload logo
  const uploadLogo = async (file: File): Promise<string | null> => {
    if (!companyId) {
      toast.error('No company associated with your account');
      return null;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const path = `logos/${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('certificates')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('certificates')
        .getPublicUrl(path);

      toast.success('Logo uploaded!');
      return data.publicUrl;
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error('Failed to upload logo');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Upload signature
  const uploadSignature = async (file: File): Promise<string | null> => {
    if (!companyId) {
      toast.error('No company associated with your account');
      return null;
    }

    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const path = `signatures/${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('certificates')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('certificates')
        .getPublicUrl(path);

      toast.success('Signature uploaded!');
      return data.publicUrl;
    } catch (err) {
      console.error('Failed to upload signature:', err);
      toast.error('Failed to upload signature');
      return null;
    } finally {
      setUploading(false);
    }
  };

  // All templates (defaults + custom)
  const allTemplates = [...defaultTemplates, ...customTemplates];

  return {
    allTemplates,
    customTemplates,
    defaultTemplates,
    loading,
    uploading,
    saveTemplate,
    deleteTemplate,
    uploadLogo,
    uploadSignature,
    refreshTemplates: loadTemplates,
  };
}
